import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FilePlus, 
  FolderOpen, 
  Save, 
  Settings, 
  BookOpen,
  Lightbulb,
  Edit3,
  MessageCircle,
  Shield,
  Play,
  CreditCard
} from 'lucide-react';
import pointsService from '../services/pointsService';
import supabaseService from '../services/supabaseService';
import authService from '../services/authService';
import ChargeModal from './ChargeModal';

// 로그 제어 플래그 (필요 시 true로 변경)
const DEBUG = false;

const SidebarContainer = styled(motion.aside)`
  width: 280px;
  background: ${props => props.theme.colors.surface};
  border-right: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SidebarTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const MenuSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  padding: 0 20px;
`;

const MenuItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: ${props => props.theme.mode === 'dark' ? '#E5E7EB' : '#111827'};
  cursor: pointer;
  transition: transform .16s cubic-bezier(.22,.61,.36,1), background-color .24s cubic-bezier(.22,.61,.36,1), box-shadow .24s cubic-bezier(.22,.61,.36,1), color .2s ease;
  border-left: 3px solid transparent;
  border-radius: 8px 0 0 8px;

  /* 아이콘이 텍스트 색을 따르도록 설정 */
  svg {
    color: inherit;
    stroke: currentColor;
    transition: opacity .2s ease, transform .16s cubic-bezier(.22,.61,.36,1), color .2s ease;
    opacity: .9;
  }

  &:hover {
    background: ${props => props.theme.mode === 'dark' ? 'rgba(15,23,42,0.7)' : 'rgba(248,250,252,0.9)'};
    border-left-color: ${props => props.theme.colors.primary};
    transform: translateX(2px);
    box-shadow: inset 0 0 0 1px ${props => props.theme.colors.border};
    svg { opacity: 1; }
  }

  &.active {
    background: ${props => props.theme.mode === 'dark' ? 'rgba(10,132,255,0.14)' : 'rgba(10,132,255,0.12)'};
    border-left-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    box-shadow: inset 0 0 0 1px ${props => props.theme.mode === 'dark' ? 'rgba(10,132,255,0.25)' : 'rgba(10,132,255,0.25)'};
    svg { opacity: 1; transform: translateX(1px); }
  }
`;

const MenuItemText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const RecentFiles = styled.div`
  flex: 1;
  padding: 20px;
  border-top: 1px solid ${props => props.theme.colors.border};
  overflow-y: auto;
  background: ${props => props.theme.mode === 'dark' ? '#0f172a' : '#f8fafc'};
`;

const RecentFileItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#1f2937' : '#eef2f7'};
    transform: translateX(2px);
  }
`;

const RecentFileName = styled.span`
  font-size: 13px;
  color: ${props => props.theme.mode === 'dark' ? '#e5e7eb' : '#111827'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RecentTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: ${p => p.theme.mode === 'dark' ? '#93c5fd' : '#1d4ed8'};
`;

const AccountSection = styled.div`
  border-top: 1px solid ${props => props.theme.colors.border};
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  user-select: none;
`;

const AccountName = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.text};
  font-weight: 600;
`;

const AccountSub = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
`;

const AccountActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button`
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
`;

const SupportTip = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 6px;
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary};
`;

function Sidebar({ currentFile, onNewDocument, onSave, onOpenFile, onToggleAI, onOpenHelp, onOpenTutorial, aiMode, onOpenAccount, account, setAccount, onOpenRecentInline, onOpenCharge, onOpenAPISettings, onOpenAdmin, onOpenLogin, recentFiles = [] }) {
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [displayPoints, setDisplayPoints] = useState(account?.points ?? 0);
  
  // 포인트 업데이트 이벤트 리스너
  useEffect(() => {
    const handlePointsUpdate = (event) => {
      if (DEBUG) console.log('🎯 Sidebar: 포인트 업데이트 이벤트 수신:', event.detail);
      const newPoints = event.detail.points;
      if (DEBUG) console.log('🎯 Sidebar: 현재 account:', account);
      if (DEBUG) console.log('🎯 Sidebar: 새로운 포인트:', newPoints);
      
      // 직접 displayPoints 상태 업데이트
      setDisplayPoints(newPoints);
      if (DEBUG) console.log('🎯 Sidebar: displayPoints 업데이트:', newPoints);
      
      setAccount(prev => {
        const updated = { ...prev, points: newPoints };
        if (DEBUG) console.log('🎯 Sidebar: account 업데이트:', prev, '→', updated);
        return updated;
      });
      
      // 강제 리렌더링
      setForceUpdate(prev => prev + 1);
      if (DEBUG) console.log('🎯 Sidebar: 강제 리렌더링 트리거');
    };
    
    window.addEventListener('pointsUpdated', handlePointsUpdate);
    
    return () => {
      window.removeEventListener('pointsUpdated', handlePointsUpdate);
    };
  }, [account, setAccount]);
  
  // account 변경 시 displayPoints 동기화
  useEffect(() => {
    if (account?.points !== undefined) {
      setDisplayPoints(account.points);
      if (DEBUG) console.log('🎯 Sidebar: account 변경 감지, displayPoints 동기화:', account.points);
    }
  }, [account?.points]);
  
  const params = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : null;
  const previewGuest = params?.get('preview') === '1';
  const adminEmail = (typeof window !== 'undefined') ? (localStorage.getItem('cover_admin_email') || '') : '';
  const isAdmin = previewGuest ? false : !!(account?.email && adminEmail && account.email === adminEmail);
  
  // 사용자 타입 확인
  const userType = pointsService.getUserType();
  const isGuest = userType === 'guest';

  const avatarChar = useMemo(() => account?.avatarChar || localStorage.getItem('olp_avatar_char') || '🧠', [account]);
  const avatarColor = useMemo(() => account?.avatarColor || localStorage.getItem('olp_avatar_color') || '#7C3AED', [account]);
  const [bump, setBump] = useState(0);

  const avatarChoices = ['🧠','📝','🦊','🐼','🐧','🐰','🐱','🐶','🐨','🐢','🐳','🦄','✨','🎓'];
  const colorChoices = ['#7C3AED','#0A84FF','#30D158','#FF9F0A','#FF375F','#FF6B6B','#5856D6','#34C759'];
  
  // 랜덤 게스트 이름 목록
  const guestNames = [
    '돌고래', '장난스런 인원', '신비로운 방문자', '활발한 탐험가', '호기심 많은 여행자',
    '빛나는 별', '웃음꽃', '달콤한 꿈', '자유로운 영혼', '행복한 바람',
    '신나는 춤꾼', '상상력 풍부한', '즐거운 친구', '따뜻한 마음', '밝은 에너지',
    '새로운 시작', '희망찬 미래', '즐거운 하루', '감사한 순간', '소중한 시간'
  ];

  const cycleAvatar = async () => {
    const idx = avatarChoices.indexOf(avatarChar);
    const next = avatarChoices[(idx + 1) % avatarChoices.length];
    localStorage.setItem('olp_avatar_char', next);
    if (account?.email && supabaseService.isConfigured()) {
      try { await supabaseService.updateUserProfile(account.email, { avatar_char: next }); } catch {}
    }
    setBump(v => v + 1);
  };

  const cycleColor = async () => {
    const idx = colorChoices.indexOf(avatarColor);
    const next = colorChoices[(idx + 1) % colorChoices.length];
    localStorage.setItem('olp_avatar_color', next);
    if (account?.email && supabaseService.isConfigured()) {
      try { await supabaseService.updateUserProfile(account.email, { avatar_color: next }); } catch {}
    }
    setBump(v => v + 1);
  };

  const displayEmail = account?.email || '';
  const localName = (typeof window !== 'undefined') ? localStorage.getItem('olp_display_name') : null;
  
  // 게스트 이름 생성 로직
  const getGuestName = () => {
    let guestName = localStorage.getItem('olp_guest_name');
    if (!guestName) {
      // 랜덤으로 게스트 이름 생성
      const randomIndex = Math.floor(Math.random() * guestNames.length);
      guestName = guestNames[randomIndex];
      localStorage.setItem('olp_guest_name', guestName);
    }
    return guestName;
  };
  
  const displayName = account?.name || localName || (isGuest ? getGuestName() : (displayEmail ? displayEmail.split('@')[0] : '게스트'));

  const handleEditName = async () => {
    if (previewGuest) return;
    const current = displayName;
    
    if (isGuest) {
      // 게스트 사용자: 랜덤 이름 선택 또는 직접 입력
      const options = [...guestNames, '직접 입력'];
      // eslint-disable-next-line no-alert
      const choice = window.prompt(`게스트 이름을 선택하세요:\n${options.map((name, i) => `${i + 1}. ${name}`).join('\n')}\n\n번호를 입력하거나 직접 이름을 입력하세요:`, current || '');
      
      if (choice == null) return;
      
      let newName;
      const choiceNum = parseInt(choice);
      if (!isNaN(choiceNum) && choiceNum >= 1 && choiceNum <= options.length) {
        if (choiceNum === options.length) {
          // 직접 입력 선택
          // eslint-disable-next-line no-alert
          const customName = window.prompt('원하는 이름을 입력하세요:', '');
          if (!customName || !customName.trim()) return;
          newName = customName.trim();
        } else {
          newName = options[choiceNum - 1];
        }
      } else {
        // 직접 입력된 것으로 간주
        newName = choice.trim();
      }
      
      if (!newName) return;
      localStorage.setItem('olp_guest_name', newName);
    } else {
      // 로그인 사용자: 기존 로직
      // eslint-disable-next-line no-alert
      const next = window.prompt('표시할 이름을 입력하세요', current || '');
      if (next == null) return;
      const trimmed = next.trim();
      if (!trimmed) return;
      localStorage.setItem('olp_display_name', trimmed);
      const acc = JSON.parse(localStorage.getItem('ai_agent_account') || '{}');
      acc.name = trimmed; localStorage.setItem('ai_agent_account', JSON.stringify(acc));
      if (account?.email && supabaseService.isConfigured()) {
        try { await supabaseService.updateUserProfile(account.email, { name: trimmed }); } catch {}
      }
    }
    
    setBump(v => v + 1);
  };

  return (
    <SidebarContainer
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <SidebarHeader>
        <SidebarTitle>도구</SidebarTitle>

        <MenuSection>
          <MenuItem
            whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }}
            onClick={previewGuest ? undefined : onNewDocument}
            style={previewGuest ? { opacity: .6, cursor: 'not-allowed' } : undefined}
          >
            <FilePlus size={18} />
            <MenuItemText>새 문서</MenuItemText>
          </MenuItem>

          <MenuItem 
            whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }}
            onClick={undefined}
            style={{ opacity: .6, cursor: 'not-allowed' }}
          >
            <FolderOpen size={18} />
            <MenuItemText>파일 열기</MenuItemText>
          </MenuItem>

          <MenuItem 
            whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }}
            onClick={onSave}
          >
            <Save size={18} />
            <MenuItemText>저장</MenuItemText>
          </MenuItem>
        </MenuSection>

        {/* AI 도우미 섹션 제거: 요청 시 바로 사이드바 채팅 열기 */}

        <MenuSection>
          <SectionTitle>기타</SectionTitle>
          
          <MenuItem whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }} onClick={onOpenTutorial}>
            <Play size={18} />
            <MenuItemText>주의 사항</MenuItemText>
          </MenuItem>

          <MenuItem whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }} onClick={onOpenHelp}>
            <BookOpen size={18} />
            <MenuItemText>도움말</MenuItemText>
          </MenuItem>

          <MenuItem whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }} onClick={() => {
            // eslint-disable-next-line no-alert
            alert('충전 후 포인트가 반영되지 않나요?\n\n해결 방법:\n1. 계정을 눌러서 로그아웃 후 다시 로그인\n2. 브라우저 새로고침 (F5 또는 Ctrl+R)\n3. 그래도 안되면 Ctrl+F5로 강력 새로고침\n\n또는 "새로고침" 버튼을 눌러주세요!');
          }}>
            <CreditCard size={18} />
            <MenuItemText>포인트 미반영 해결 방법</MenuItemText>
          </MenuItem>

          <MenuItem whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }} onClick={previewGuest ? undefined : onOpenAPISettings} style={previewGuest ? { opacity: .6, cursor: 'not-allowed' } : undefined}>
            <Settings size={18} />
            <MenuItemText>AI 모드 및 언어 선택</MenuItemText>
          </MenuItem>
          {isAdmin && (
            <MenuItem whileHover={{ x: 4, transition: { type: 'tween', ease: [0.22,0.61,0.36,1], duration: 0.18 } }} onClick={onOpenAdmin}>
              <Shield size={18} />
              <MenuItemText>관리자</MenuItemText>
            </MenuItem>
          )}
        </MenuSection>
      </SidebarHeader>

      {/* 최근 파일 섹션 제거 */}

      <AccountSection>
        <AccountRow>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar style={{ background: avatarColor, cursor: 'pointer' }} onClick={cycleAvatar} title="아바타 변경">
              <span>{avatarChar}</span>
            </Avatar>
            <div>
              <AccountName style={{ cursor: previewGuest ? 'default' : 'pointer' }} onClick={handleEditName} title={previewGuest ? undefined : '이름 편집'}>
                {displayName}
              </AccountName>
              <AccountSub>{account?.email || (isGuest ? '게스트 사용자' : '')}</AccountSub>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {isGuest ? (
              <SmallButton onClick={previewGuest ? undefined : onOpenLogin} disabled={previewGuest}>로그인</SmallButton>
            ) : (
              <SmallButton onClick={previewGuest ? undefined : onOpenAccount} disabled={previewGuest}>계정</SmallButton>
            )}
          </div>
        </AccountRow>
        <BalanceRow>
          <span>포인트</span>
          <span style={{ fontWeight: 'bold', color: '#0A84FF' }}>{displayPoints.toLocaleString()} P</span>
        </BalanceRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <SmallButton 
            onClick={isGuest ? onOpenLogin : () => setIsChargeModalOpen(true)} 
            disabled={isGuest}
            title={isGuest ? "로그인 후 충전 가능" : "포인트 충전"}
          >
            {isGuest ? "로그인 후 충전" : "충전"}
          </SmallButton>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <SmallButton
            onClick={() => window.open('http://pf.kakao.com/_ixajxmn/friend', '_blank')}
            title="카카오채널 열기"
          >
            카카오채널
          </SmallButton>
          <SmallButton
            onClick={() => {
              const email = 'ckdgnssla12@gmail.com';
              window.location.href = `mailto:${email}`;
            }}
            title="개발자 이메일"
          >
            이메일 문의
          </SmallButton>
          <SmallButton
            onClick={() => window.open('https://www.instagram.com/onelinepaper?igsh=MTBqdnR4ODN3bTFiNA%3D%3D&utm_source=qr', '_blank')}
            title="인스타 열기"
          >
            인스타
          </SmallButton>
        </div>
        <SupportTip>
          💬 실시간 상담은 화면 좌하단 버튼을 눌러 이용할 수 있어요.
        </SupportTip>
      </AccountSection>
      
      {/* 충전 모달 */}
      <ChargeModal 
        isOpen={isChargeModalOpen}
        onClose={() => setIsChargeModalOpen(false)}
        theme="light"
      />
    </SidebarContainer>
  );
}

export default Sidebar;
